/**
 * Full view — a list fills the whole browser window (client request, 14 Oct 2026: "table can
 * expand to full view … detach and fully fit the current window").
 *
 * `useFullView()` holds the on/off for one screen; `<FullViewScope>` wraps what enlarges — the
 * page's header WITH its filters and the table (the client wanted the filters along), or a report
 * card that carries its own toolbar — and `<FullViewButton>` (rendered by `FreezeColumnsControl`
 * beside the freeze choice) switches it. `Esc` leaves, unless a dialog or an open dropdown is
 * what the Esc is for. Not remembered: a full view is for now, and a page that opened enlarged
 * next week would be a surprise.
 *
 * ── Filling the height exactly ──────────────────────────────────────────────
 * A list's scroll box is one screen tall less the top bar (`LIST_SCROLL_Y`, the dense table's
 * sticky box). In full view the box should run from where it starts to the bottom of the window,
 * which depends on how tall the header and filters above it are. So the scope MEASURES its first
 * scroll box (`.ant-table-body`, or a dense table's `[data-cm-sticky-box]`) and sets
 * `--cm-fv-avail` to the height left below it; both maxima read that variable first and fall back
 * to their page values outside a full view. Measured after every render and on resize, in CSS
 * pixels (divided by the text-size zoom, CLAUDE.md, Web).
 *
 * The filters come along, and on some screens they are tall (the Audit Log's filter panel ends
 * two-thirds down a laptop screen), so "the height left below" could leave a sliver of rows. The
 * box is therefore never shorter than 60% of the window, and on entering a full view the scope
 * scrolls just far enough to show the whole box — the filters are one scroll up. Where everything
 * fits (the Worker Register) nothing scrolls.
 *
 * "Detach" into a separate browser window was rejected: that window would lose the page's
 * filters, saving and selection, all of which would have to be rebuilt per list.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Button, Tooltip } from 'antd';
import { FullscreenExitOutlined, FullscreenOutlined } from '@ant-design/icons';
import { pageZoom } from '@/lib/text-size';
import './full-view.css';

export interface FullViewState {
  full: boolean;
  toggle: () => void;
  exit: () => void;
}

export function useFullView(): FullViewState {
  const [full, setFull] = useState(false);
  const toggle = useCallback(() => setFull((f) => !f), []);
  const exit = useCallback(() => setFull(false), []);
  return useMemo(() => ({ full, toggle, exit }), [full, toggle, exit]);
}

/** Space kept under the scroll box: the bottom pager and the body's padding. */
const BOTTOM_RESERVE = 64;
/** The box is never shorter than this share of the window, however tall the filters above. */
const MIN_BOX_SHARE = 0.6;

export function FullViewScope({
  view,
  children,
  padded = false,
}: {
  view: FullViewState;
  children: ReactNode;
  /** A card that carries no page padding of its own gets the body's gutter when enlarged. */
  padded?: boolean;
}) {
  const ref = useRef<HTMLDivElement | null>(null);

  const measure = useRef<() => void>(() => undefined);
  measure.current = () => {
    const scope = ref.current;
    if (!scope) return;
    if (!view.full) {
      scope.style.removeProperty('--cm-fv-avail');
      return;
    }
    const box = scope.querySelector<HTMLElement>('.ant-table-body, [data-cm-sticky-box]');
    if (!box) return;
    const zoom = pageZoom();
    const top = (box.getBoundingClientRect().top - scope.getBoundingClientRect().top) / zoom + scope.scrollTop;
    const windowH = window.innerHeight / zoom;
    const avail = Math.max(Math.floor(windowH * MIN_BOX_SHARE), Math.floor(windowH - top - BOTTOM_RESERVE));
    scope.style.setProperty('--cm-fv-avail', `${avail}px`);
  };

  useLayoutEffect(() => {
    measure.current();
  });

  const { full, exit } = view;
  /* Keyed on the on/off and the stable `exit` only: re-running per render would re-add the
   * listeners and jump the view back to the top while someone types in a filter. */
  useEffect(() => {
    if (!full) return;
    const onResize = () => measure.current();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      /* The Esc belongs to a dialog or an open dropdown, not to the full view. */
      if (document.querySelector('.ant-modal-wrap:not([style*="display: none"]) .ant-modal, .ant-select-dropdown:not(.ant-select-dropdown-hidden), .ant-picker-dropdown:not(.ant-picker-dropdown-hidden), .ant-popover:not(.ant-popover-hidden)')) {
        return;
      }
      exit();
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('keydown', onKey);
    /* Start at the top, then scroll just enough that the whole box — sized by the layout effect
     * that has already run — is on screen above the bottom pager. */
    const scope = ref.current;
    if (scope) scope.scrollTop = 0;
    /* A frame later: antd applies the body's new max-height on its own render, after ours. */
    const frame = requestAnimationFrame(() => {
      const box = scope?.querySelector<HTMLElement>('.ant-table-body, [data-cm-sticky-box]');
      if (!scope || !box) return;
      const zoom = pageZoom();
      const overflow = (box.getBoundingClientRect().bottom - window.innerHeight) / zoom + BOTTOM_RESERVE;
      if (overflow > 0) scope.scrollTop = overflow;
    });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('keydown', onKey);
    };
  }, [full, exit]);

  return (
    <div
      ref={ref}
      className={view.full ? 'cm-fullview' : undefined}
      style={view.full && padded ? { padding: '16px 20px' } : undefined}
      data-cm-fullview-scope=""
    >
      {children}
    </div>
  );
}

/** `Full view` / `Exit full view` — beside a list's `Freeze columns`. */
export function FullViewButton({ view }: { view: FullViewState }) {
  return (
    <Tooltip title={view.full ? 'Back to the normal page (Esc)' : 'Fill the window with this list. Esc to leave.'}>
      <Button
        size="small"
        icon={view.full ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
        onClick={view.toggle}
        aria-pressed={view.full}
      >
        {view.full ? 'Exit full view' : 'Full view'}
      </Button>
    </Tooltip>
  );
}
